import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import LessonChip from './LessonChip';
import WorksheetChip from './WorksheetChip';
import styles from './WeekView.module.css';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getWeekDays(date) {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function dateKey(date) {
  return date.toISOString().split('T')[0];
}

function isWeekendDay(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function WeekDayColumn({ day, dayLessons, dayWorksheets = [], isToday, holiday, activeUnit, onDayClick, onLessonClick }) {
  const key = dateKey(day);
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${key}`,
    data: { date: day, type: 'day' },
  });

  const columnClass = [
    styles.dayColumn,
    isToday ? styles.today : '',
    holiday ? styles.holiday : '',
    isOver ? styles.dropTarget : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={setNodeRef}
      className={columnClass}
      onClick={() => onDayClick(day, dayLessons)}
    >
      <div className={styles.dayHeader}>
        <span className={styles.dayName}>{DAY_NAMES[day.getDay()]}</span>
        <span className={`${styles.dateNumber} ${isToday ? styles.dateToday : ''}`}>
          {day.getDate()}
        </span>
        {holiday && (
          <span className={styles.holidayBadge}>
            {holiday.emoji} {holiday.name}
          </span>
        )}
        {activeUnit && (
          <span
            className={styles.unitBadge}
            style={{
              background: activeUnit.color.bg,
              borderColor: activeUnit.color.border,
              color: activeUnit.color.text,
            }}
          >
            {activeUnit.title}
          </span>
        )}
      </div>
      <div className={styles.lessons}>
        {dayLessons.map((lesson) => (
          <LessonChip
            key={lesson.id}
            lesson={lesson}
            compact={false}
            onClick={onLessonClick}
          />
        ))}
        {dayWorksheets.map((worksheet) => (
          <WorksheetChip
            key={`ws-${worksheet.id}`}
            worksheet={worksheet}
          />
        ))}
        {dayLessons.length === 0 && dayWorksheets.length === 0 && (
          <span className={styles.empty}>No lessons</span>
        )}
      </div>
    </div>
  );
}

// Unit band color palette (same as MonthView)
const UNIT_COLORS = [
  { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' },
  { bg: '#f0fdf4', border: '#22c55e', text: '#166534' },
  { bg: '#fdf4ff', border: '#a855f7', text: '#6b21a8' },
  { bg: '#fff7ed', border: '#f97316', text: '#9a3412' },
  { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
  { bg: '#ecfeff', border: '#06b6d4', text: '#155e75' },
  { bg: '#fefce8', border: '#eab308', text: '#854d0e' },
  { bg: '#f0fdfa', border: '#14b8a6', text: '#115e59' },
  { bg: '#faf5ff', border: '#8b5cf6', text: '#5b21b6' },
];

function getUnitForDate(dateKey, units) {
  if (!units || units.length === 0) return null;
  for (const unit of units) {
    if (!unit.startDate || !unit.endDate) continue;
    const start = new Date(unit.startDate).toISOString().split('T')[0];
    const end = new Date(unit.endDate).toISOString().split('T')[0];
    if (dateKey >= start && dateKey <= end) {
      const colorIndex = ((unit.sequenceOrder || 1) - 1) % UNIT_COLORS.length;
      return { ...unit, color: UNIT_COLORS[colorIndex] };
    }
  }
  return null;
}

function WeekView({ currentDate, lessons, worksheets = [], units, onDayClick, onLessonClick, showWeekends, holidayMap }) {
  const allDays = getWeekDays(currentDate);
  const days = showWeekends ? allDays : allDays.filter(d => !isWeekendDay(d));
  const columnsPerRow = days.length;
  const today = new Date();
  const todayStr = dateKey(today);

  // Group lessons by date
  const lessonsByDate = {};
  for (const lesson of lessons) {
    if (lesson.scheduledDate) {
      const key = new Date(lesson.scheduledDate).toISOString().split('T')[0];
      if (!lessonsByDate[key]) lessonsByDate[key] = [];
      lessonsByDate[key].push(lesson);
    }
  }

  // Group worksheets by date
  const worksheetsByDate = {};
  for (const worksheet of worksheets) {
    if (worksheet.scheduledDate) {
      const key = new Date(worksheet.scheduledDate).toISOString().split('T')[0];
      if (!worksheetsByDate[key]) worksheetsByDate[key] = [];
      worksheetsByDate[key].push(worksheet);
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.grid} style={{ gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)` }}>
        {days.map((day) => {
          const key = dateKey(day);
          const dayLessons = lessonsByDate[key] || [];
          const isToday = key === todayStr;

          const activeUnit = getUnitForDate(key, units);

          return (
            <WeekDayColumn
              key={key}
              day={day}
              dayLessons={dayLessons}
              dayWorksheets={worksheetsByDate[key] || []}
              isToday={isToday}
              holiday={holidayMap[key] || null}
              activeUnit={activeUnit}
              onDayClick={onDayClick}
              onLessonClick={onLessonClick}
            />
          );
        })}
      </div>
    </div>
  );
}

export default WeekView;
